// MemoryPage — Flame King "Brain".
//
// The agent's memory, one surface. Jarvis only (the single Flame King agent),
// four lenses:
//   1. Session Logs      — type=session_log, the append-only diary.
//   2. Learnings         — type=learning OR principle, append-only lessons.
//   3. Session Summary   — living kb-doc/agents/jarvis/session-summary.
//   4. Learnings Summary — living kb-doc/agents/jarvis/learnings-summary.
//
// Logs come from /api/memories (rows the agent writes via psql every few
// turns). Summaries are KB pages rendered through the BlockRenderer. Modeled
// on the reference MyJarvis dashboard, trimmed to one agent.

import { useEffect, useMemo, useState } from "react";
import { Brain, AlertCircle, ScrollText, Lightbulb, FileText, BookOpen } from "lucide-react";
import { useApi } from "@/lib/api";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";

const AGENT = "jarvis";
const AGENT_LABEL = "Jarvis";

type MemoryRow = {
  id: string;
  agent: string | null;
  type: string;
  title: string | null;
  body: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

type TabKey = "logs" | "learnings" | "session_summary" | "learnings_summary";

const TABS: { key: TabKey; label: string; icon: typeof Brain }[] = [
  { key: "session_summary", label: "Session Summary", icon: FileText },
  { key: "learnings_summary", label: "Learning Summary", icon: BookOpen },
  { key: "logs", label: "Session Log", icon: ScrollText },
  { key: "learnings", label: "Learning Log", icon: Lightbulb },
];

const TYPE_STYLES: Record<string, string> = {
  session_log: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  learning: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  principle: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
};

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function MemoryList({ rows, emptyHint }: { rows: MemoryRow[] | null; emptyHint: string }) {
  if (rows === null) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border bg-muted/30 p-8 text-center">
        <p className="text-sm font-medium">Nothing here yet</p>
        <p className="mt-1 text-xs text-muted-foreground">{emptyHint}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {rows.map((m) => (
        <div
          key={m.id}
          className="rounded-xl border bg-card p-4 transition-colors hover:border-foreground/20"
        >
          <div className="mb-2 flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  TYPE_STYLES[m.type] ?? "bg-muted text-muted-foreground"
                }`}
              >
                {m.type.replace("_", " ")}
              </span>
              {m.title && <span className="text-sm font-medium">{m.title}</span>}
            </div>
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {formatTimestamp(m.created_at)}
            </span>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/90">
            {m.body}
          </pre>
        </div>
      ))}
    </div>
  );
}

// Renders a living KB summary page (Session / Learnings) through BlockRenderer.
function SummaryView({ kind }: { kind: "session-summary" | "learnings-summary" }) {
  const api = useApi();
  const slug = `kb-doc/agents/${AGENT}/${kind}`;
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBlocks(null);
    setMissing(false);
    setError(null);
    (async () => {
      try {
        const res = await api(`/api/kb/${slug}`);
        if (res.status === 404) {
          if (!cancelled) setMissing(true);
          return;
        }
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const body = (await res.json()) as { content?: { blocks?: Block[] } };
        if (cancelled) return;
        if (Array.isArray(body.content?.blocks)) {
          setBlocks(body.content?.blocks ?? null);
        } else {
          setMissing(true);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Failed to load summary: {error}</span>
      </div>
    );
  }
  if (missing) {
    const kindLabel = kind === "session-summary" ? "Session Summary" : "Learnings Summary";
    return (
      <div className="rounded-xl border bg-muted/30 p-8 text-center">
        <Brain className="mx-auto mb-2 h-8 w-8 text-muted-foreground/60" />
        <p className="text-sm font-medium">
          No {kindLabel.toLowerCase()} bootstrapped yet for {AGENT_LABEL}.
        </p>
      </div>
    );
  }
  if (blocks === null) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  return <BlockRenderer config={architectureConfig} blocks={blocks} />;
}

export function MemoryPage() {
  const api = useApi();
  const [tab, setTab] = useState<TabKey>("session_summary");
  const [rows, setRows] = useState<MemoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    (async () => {
      try {
        const res = await api(`/api/memories?agent=${encodeURIComponent(AGENT)}`);
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as MemoryRow[];
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logs = useMemo(
    () => rows?.filter((m) => m.type === "session_log") ?? null,
    [rows],
  );
  const learnings = useMemo(
    () => rows?.filter((m) => m.type === "learning" || m.type === "principle") ?? null,
    [rows],
  );

  return (
    <div className="w-full h-svh overflow-auto bg-background">
      <div className="border-b px-6 py-5 flex items-center gap-3">
        <Brain className="h-5 w-5 text-foreground/80" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold leading-tight">The Brain</h1>
          <p className="text-xs text-muted-foreground">
            {AGENT_LABEL}'s memory — logs, learnings, and the living summaries.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b px-6 flex flex-wrap items-center gap-0.5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="px-6 py-5">
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Failed to load: {error}</span>
          </div>
        )}

        {tab === "logs" && (
          <MemoryList
            rows={logs}
            emptyHint={`Session logs for ${AGENT_LABEL} appear here as the agent saves them every few turns.`}
          />
        )}
        {tab === "learnings" && (
          <MemoryList
            rows={learnings}
            emptyHint={`Distilled learnings + principles for ${AGENT_LABEL} surface here.`}
          />
        )}
        {tab === "session_summary" && <SummaryView kind="session-summary" />}
        {tab === "learnings_summary" && <SummaryView kind="learnings-summary" />}
      </div>
    </div>
  );
}
